import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { passwordResetApi } from '../hooks/usePasswordResetApi';
import { ApiClientError } from '../lib/api-client';
import logo from '../assets/images/logo.svg';

type VerificationState = 'pending' | 'verifying' | 'success' | 'error';

/**
 * VerifyEmail — page-level component for the `/verify-email` route.
 *
 * Reads the verification token from the `?token=` query string and
 * redeems it via the BFF on mount. Idempotent — refreshing the page
 * after success simply shows the success state again because the auth
 * service flips `User.emailVerified=true` exactly once per token.
 *
 * @see HZVT-150_ADD_AUTH_PASSWORD_RESET_AND_EMAIL_VERIFICATION
 */
export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') ?? '';

  const [state, setState] = useState<VerificationState>(tokenFromUrl ? 'verifying' : 'pending');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!tokenFromUrl) return;

    void (async () => {
      try {
        await passwordResetApi.verifyEmail({ token: tokenFromUrl });
        if (active) setState('success');
      } catch (err) {
        if (!active) return;
        setState('error');
        if (err instanceof ApiClientError && err.statusCode === 400) {
          setError('This verification link is invalid or has expired.');
        } else {
          setError('Something went wrong while verifying your email.');
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [tokenFromUrl]);

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="flex justify-center mb-6">
          <img src={logo} alt="ZuroQ Logo" className="h-12 w-auto" />
        </div>
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">Verify Email</h1>

        {state === 'pending' && (
          <p className="text-center text-gray-600 text-sm mb-8" data-testid="verify-email-pending">
            Open the link in your verification email to confirm this address.
          </p>
        )}

        {state === 'verifying' && (
          <p className="text-center text-gray-600 text-sm mb-8" data-testid="verify-email-loading">
            Verifying your email...
          </p>
        )}

        {state === 'success' && (
          <div
            data-testid="verify-email-success"
            className="bg-green-50 border border-green-200 rounded-md p-4 text-center text-green-800 mb-6"
          >
            <p className="font-medium">Email verified.</p>
            <p className="text-sm mt-1">Thanks for confirming your address.</p>
          </div>
        )}

        {state === 'error' && (
          <div
            data-testid="verify-email-error"
            className="bg-red-50 border border-red-200 rounded-md p-4 text-center text-red-800 mb-6"
          >
            <p className="font-medium">Verification failed.</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        <Link to="/login">
          <Button className="w-full">Back to login</Button>
        </Link>
      </div>
    </div>
  );
};
